import React, { useState, useEffect } from "react";
import {
  Box, Typography, IconButton, Avatar, Select, MenuItem,
  FormControl, CircularProgress, Snackbar, Alert, Tooltip, useTheme, Chip, LinearProgress,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { backendClient } from "../../../../api/backend";
import { useSelector } from "react-redux";
import ShareContractModal from "../../components/ShareContractModal";
import { useTranslation } from "react-i18next";
import ReactGA from 'react-ga4';
import { tracker } from '../../../../analytics';

export const CollaboratorRoleEnum = {
  CREATOR: "creator",
  VIEWER: "viewer",
  EDITOR: "editor",
  REVIEWER: "reviewer",
  SIGNATOR: "signator"
};

const getInitials = (name) =>
  name.split(' ').map(p => p.charAt(0)).join('').toUpperCase().slice(0, 2);

const AVATAR_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#14b8a6',
];
const getAvatarColor = (name) => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

const Collaboration = ({ contractId }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useTranslation();
  const [collaborators, setCollaborators] = useState([]);
  const [pendingExternalInvitations, setPendingExternalInvitations] = useState([]);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const currentUser = useSelector(state => state.auth?.user?.user);
  const reduxContract = useSelector(state => state.contract?.contract);

  // On initial mount, hydrate from Redux if the contract is already loaded;
  // otherwise fall back to a direct fetch. After mutations, fetchExistingCollaborators
  // is still used to refresh.
  useEffect(() => {
    if (!contractId) return;
    if (reduxContract && reduxContract.id === contractId) {
      setCollaborators((reduxContract.collaborators || []).map(c => ({
        id: c.id,
        name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email,
        email: c.email,
        role: c.role,
        review_status: c.review_status || null,
        review_completed_at: c.review_completed_at || null,
        is_external: c.is_external || false,
      })));
      setPendingExternalInvitations(reduxContract.pending_external_invitations || []);
    } else {
      fetchExistingCollaborators();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, reduxContract?.id]);

  const fetchExistingCollaborators = async () => {
    try {
      setIsSaving(true);
      const data = await backendClient.getContract(contractId);
      if (data?.collaborators) {
        setCollaborators(data.collaborators.map(c => ({
          id: c.id,
          name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email,
          email: c.email,
          role: c.role,
          review_status: c.review_status || null,
          review_completed_at: c.review_completed_at || null,
          is_external: c.is_external || false,
        })));
      }
      if (data?.pending_external_invitations) {
        setPendingExternalInvitations(data.pending_external_invitations);
      } else {
        setPendingExternalInvitations([]);
      }
    } catch (err) {
      setAlertMessage("Unable to load collaborators.");
      setShowErrorAlert(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleShare = async (selectedUsers) => {
    if (!selectedUsers.length) return;
    setIsSaving(true);
    try {
      await backendClient.shareContract(contractId, selectedUsers.map(u => ({ user_id: u.id, role: u.role })));
      const updated = [...collaborators];
      selectedUsers.forEach(u => {
        if (!updated.some(c => c.id === u.id)) {
          updated.push({
            id: u.id,
            name: `${u.first_name} ${u.last_name}`,
            email: u.email || "",
            role: u.role,
            review_status: u.role === 'reviewer' ? 'pending' : null,
            review_completed_at: null,
          });
        }
      });
      setCollaborators(updated);
      ReactGA.event({ category: 'Collaboration', action: 'Add Collaborators', label: `Added ${selectedUsers.length} user(s)` });
      tracker.event({ category: 'Collaboration', action: 'Add Collaborators', label: `Added ${selectedUsers.length} user(s)` });
      setIsShareModalOpen(false);
      // Refresh to pick up any external invitations added by the modal
      await fetchExistingCollaborators();
      setAlertMessage(t('successAdd'));
      setShowSuccessAlert(true);
    } catch (err) {
      setAlertMessage(t("errorAdd"));
      setShowErrorAlert(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (collaboratorId, name) => {
    setIsSaving(true);
    try {
      setCollaborators(prev => prev.filter(c => c.id !== collaboratorId));
      await backendClient.removeCollaborator(contractId, collaboratorId);
      ReactGA.event({ category: 'Collaboration', action: 'Remove Collaborator', label: name });
      tracker.event({ category: 'Collaboration', action: 'Remove Collaborator', label: name });
      setAlertMessage(t('successRemove').replace("{name}", name));
      setShowSuccessAlert(true);
    } catch (err) {
      fetchExistingCollaborators();
      setAlertMessage(t('errorRemove').replace("{name}", name));
      setShowErrorAlert(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRoleChange = async (collaboratorId, name, newRole) => {
    setIsSaving(true);
    try {
      setCollaborators(prev => prev.map(c =>
        c.id === collaboratorId
          ? { ...c, role: newRole, review_status: newRole === 'reviewer' ? 'pending' : null, review_completed_at: null }
          : c
      ));
      await backendClient.updateCollaboratorRole(contractId, collaboratorId, newRole);
      ReactGA.event({ category: 'Collaboration', action: 'Update Collaborator Role', label: `${name} -> ${newRole}` });
      tracker.event({ category: 'Collaboration', action: 'Update Collaborator Role', label: `${name} -> ${newRole}` });
      setAlertMessage(t('successUpdate').replace("{name}", name));
      setShowSuccessAlert(true);
    } catch (err) {
      fetchExistingCollaborators();
      setAlertMessage(t('errorUpdate').replace("{name}", name));
      setShowErrorAlert(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetReview = async (collaboratorId, name) => {
    setIsSaving(true);
    try {
      setCollaborators(prev => prev.map(c =>
        c.id === collaboratorId ? { ...c, review_status: 'pending', review_completed_at: null } : c
      ));
      await backendClient.resetReview(contractId, collaboratorId);
      setAlertMessage(t('review_reset_success', 'Relecture réinitialisée pour {name}').replace("{name}", name));
      setShowSuccessAlert(true);
    } catch (err) {
      fetchExistingCollaborators();
      setAlertMessage(t('review_reset_error', 'Erreur lors de la réinitialisation').replace("{name}", name));
      setShowErrorAlert(true);
    } finally {
      setIsSaving(false);
    }
  };

  const sorted = [...collaborators].sort((a, b) => {
    if (a.role === CollaboratorRoleEnum.CREATOR) return -1;
    if (b.role === CollaboratorRoleEnum.CREATOR) return 1;
    return 0;
  });

  // Review progress data
  const reviewers = collaborators.filter(c => c.role === CollaboratorRoleEnum.REVIEWER);
  const completedReviewers = reviewers.filter(c => c.review_status === 'completed');
  const isCreator = collaborators.some(c => c.id === currentUser?.id && c.role === CollaboratorRoleEnum.CREATOR);
  const canManageRoles = collaborators.some(c => c.id === currentUser?.id && (c.role === CollaboratorRoleEnum.CREATOR || c.role === CollaboratorRoleEnum.EDITOR));

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* ── Header ── */}
      <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
              {t('sectionTitle')}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary, mt: 0.25 }}>
              {collaborators.length + pendingExternalInvitations.length} {t('collaborators', 'collaborateurs')}
            </Typography>
          </Box>
          {isSaving && <CircularProgress size={16} sx={{ mt: 0.5 }} />}
        </Box>
      </Box>

      {/* ── Collaborators list ── */}
      <Box sx={{
        flexGrow: 1, overflowY: 'auto',
        '&::-webkit-scrollbar': { width: '3px' },
        '&::-webkit-scrollbar-thumb': { background: alpha(theme.palette.text.secondary, 0.18), borderRadius: '4px' },
      }}>
        {collaborators.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 1.5 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: '12px',
              backgroundColor: alpha(theme.palette.text.secondary, 0.08),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <GroupOutlinedIcon sx={{ fontSize: 24, color: theme.palette.text.disabled }} />
            </Box>
            <Typography sx={{ fontSize: '0.82rem', color: theme.palette.text.secondary }}>
              {t('noCollaborators')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {sorted.map((collab) => {
              const isCollabCreator = collab.role === CollaboratorRoleEnum.CREATOR;
              const isReviewer = collab.role === CollaboratorRoleEnum.REVIEWER;
              return (
                <Box
                  key={collab.id}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25,
                    px: 1, py: 1,
                    borderRadius: '10px',
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    backgroundColor: isDark ? alpha('#fff', 0.02) : '#fff',
                    transition: 'background-color 0.15s',
                    '&:hover': { backgroundColor: isDark ? alpha('#fff', 0.04) : alpha('#000', 0.02) },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 34, height: 34, flexShrink: 0,
                      backgroundColor: getAvatarColor(collab.name),
                      fontSize: '0.75rem', fontWeight: 700,
                    }}
                  >
                    {getInitials(collab.name)}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: theme.palette.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {collab.name}
                      </Typography>
                      {isCollabCreator && (
                        <Typography component="span" sx={{ fontSize: '0.68rem', color: theme.palette.primary.main, fontWeight: 600, flexShrink: 0 }}>
                          · {t('creator')}
                        </Typography>
                      )}
                      {collab.is_external && (
                        <Chip
                          size="small"
                          label={t('shareContractModal.externalBadge', 'Externe')}
                          sx={{
                            height: 16, fontSize: '0.58rem', fontWeight: 700,
                            ml: 0.5, flexShrink: 0,
                            backgroundColor: alpha('#6366f1', 0.12),
                            color: '#6366f1',
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '0.72rem', color: theme.palette.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {collab.email}
                      </Typography>
                      {isReviewer && collab.review_status && (
                        <Chip
                          size="small"
                          icon={collab.review_status === 'completed'
                            ? <CheckCircleOutlineIcon sx={{ fontSize: 12 }} />
                            : <HourglassEmptyIcon sx={{ fontSize: 12 }} />
                          }
                          label={collab.review_status === 'completed' ? t('review_completed', 'Terminé') : t('review_pending', 'En attente')}
                          sx={{
                            height: 18,
                            fontSize: '0.62rem',
                            fontWeight: 600,
                            flexShrink: 0,
                            ...(collab.review_status === 'completed' ? {
                              backgroundColor: alpha('#10b981', 0.12),
                              color: '#10b981',
                              '& .MuiChip-icon': { color: '#10b981' },
                            } : {
                              backgroundColor: alpha('#f59e0b', 0.12),
                              color: '#f59e0b',
                              '& .MuiChip-icon': { color: '#f59e0b' },
                            }),
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {!isCollabCreator && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      <FormControl size="small" variant="outlined">
                        <Select
                          value={collab.role}
                          onChange={(e) => handleRoleChange(collab.id, collab.name, e.target.value)}
                          disabled={isSaving || !canManageRoles}
                          sx={{
                            height: 28, fontSize: '0.72rem', fontWeight: 500,
                            color: theme.palette.text.secondary,
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(theme.palette.divider, 0.7), borderRadius: '7px' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(theme.palette.text.secondary, 0.5) },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                            '& .MuiSelect-select': { py: '4px', pr: '24px !important', pl: 1 },
                          }}
                        >
                          <MenuItem value={CollaboratorRoleEnum.VIEWER} sx={{ fontSize: '0.78rem' }}>{t('viewer')}</MenuItem>
                          <MenuItem value={CollaboratorRoleEnum.EDITOR} sx={{ fontSize: '0.78rem' }}>{t('editor')}</MenuItem>
                          <MenuItem value={CollaboratorRoleEnum.REVIEWER} sx={{ fontSize: '0.78rem' }}>{t('reviewer')}</MenuItem>
                        </Select>
                      </FormControl>

                      {/* Reset review button for creator when reviewer has completed */}
                      {isCreator && isReviewer && collab.review_status === 'completed' && (
                        <Tooltip title={t('reset_review', 'Réinitialiser')}>
                          <IconButton
                            onClick={() => handleResetReview(collab.id, collab.name)}
                            size="small"
                            disabled={isSaving}
                            sx={{
                              width: 28, height: 28, borderRadius: '7px',
                              color: alpha('#f59e0b', 0.8),
                              border: `1px solid ${alpha('#f59e0b', 0.3)}`,
                              transition: 'all 0.15s',
                              '&:hover': {
                                color: '#f59e0b',
                                borderColor: alpha('#f59e0b', 0.5),
                                backgroundColor: alpha('#f59e0b', 0.06),
                              },
                              '&.Mui-disabled': { opacity: 0.3 },
                            }}
                          >
                            <RestartAltIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      )}

                      {canManageRoles && <Tooltip title={t('remove', 'Retirer')}>
                        <IconButton
                          onClick={() => handleDelete(collab.id, collab.name)}
                          size="small"
                          disabled={isSaving}
                          sx={{
                            width: 28, height: 28, borderRadius: '7px',
                            color: alpha(theme.palette.text.secondary, 0.6),
                            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                            transition: 'all 0.15s',
                            '&:hover': {
                              color: theme.palette.error.main,
                              borderColor: alpha(theme.palette.error.main, 0.4),
                              backgroundColor: alpha(theme.palette.error.main, 0.06),
                            },
                            '&.Mui-disabled': { opacity: 0.3 },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* ── Pending External Invitations ── */}
        {pendingExternalInvitations.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: collaborators.length > 0 ? 0.5 : 0 }}>
            {pendingExternalInvitations.map((inv) => (
              <Box
                key={inv.id}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.25,
                  px: 1, py: 1,
                  borderRadius: '10px',
                  border: `1px dashed ${alpha('#6366f1', 0.3)}`,
                  backgroundColor: isDark ? alpha('#6366f1', 0.04) : alpha('#6366f1', 0.02),
                }}
              >
                <Avatar
                  sx={{
                    width: 34, height: 34, flexShrink: 0,
                    backgroundColor: alpha('#6366f1', 0.15),
                    color: '#6366f1',
                    fontSize: '0.85rem',
                  }}
                >
                  <MailOutlineIcon sx={{ fontSize: 18 }} />
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: theme.palette.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {inv.email}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                      size="small"
                      label={t('shareContractModal.externalBadge', 'Externe')}
                      sx={{
                        height: 16, fontSize: '0.58rem', fontWeight: 700,
                        backgroundColor: alpha('#6366f1', 0.12),
                        color: '#6366f1',
                      }}
                    />
                    <Chip
                      size="small"
                      icon={<HourglassEmptyIcon sx={{ fontSize: 10 }} />}
                      label={t('collaboration.pendingInvite', 'En attente')}
                      sx={{
                        height: 16, fontSize: '0.58rem', fontWeight: 600,
                        backgroundColor: alpha('#f59e0b', 0.12),
                        color: '#f59e0b',
                        '& .MuiChip-icon': { color: '#f59e0b' },
                      }}
                    />
                  </Box>
                </Box>

                <Typography sx={{ fontSize: '0.68rem', color: theme.palette.text.secondary, flexShrink: 0 }}>
                  {t(`shareContractModal.role${inv.role.charAt(0).toUpperCase() + inv.role.slice(1)}`, inv.role)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* ── Review Progress Panel (visible when reviewers exist) ── */}
        {reviewers.length > 0 && (
          <Box sx={{
            mt: 2, p: 1.5, borderRadius: '10px',
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            backgroundColor: isDark ? alpha('#fff', 0.02) : alpha('#000', 0.01),
          }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', mb: 1 }}>
              {t('review_progress', 'Progression de la relecture')}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={reviewers.length > 0 ? (completedReviewers.length / reviewers.length) * 100 : 0}
                sx={{
                  flex: 1, height: 6, borderRadius: 3,
                  backgroundColor: alpha(theme.palette.divider, 0.3),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                    backgroundColor: completedReviewers.length === reviewers.length ? '#10b981' : '#c9a96e',
                  },
                }}
              />
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: theme.palette.text.secondary, flexShrink: 0 }}>
                {completedReviewers.length}/{reviewers.length}
              </Typography>
            </Box>

            {completedReviewers.length === reviewers.length && reviewers.length > 0 && (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5,
                borderRadius: '7px',
                backgroundColor: alpha('#10b981', 0.08),
              }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 14, color: '#10b981' }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#10b981' }}>
                  {t('all_reviews_complete', 'Toutes les relectures sont terminées')}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* ── Add button ── */}
      <Box sx={{ pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`, flexShrink: 0 }}>
        <Box
          component="button"
          onClick={() => {
            ReactGA.event({ category: 'Collaboration', action: 'Open Share Collaborator Modal' });
            tracker.event({ category: 'Collaboration', action: 'Open Share Collaborator Modal' });
            setIsShareModalOpen(true);
          }}
          disabled={isSaving}
          sx={{
            width: '100%', height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 1, borderRadius: '9px', cursor: 'pointer',
            fontFamily: theme.typography.fontFamily, fontSize: '0.875rem', fontWeight: 600,
            background: isDark ? alpha('#fff', 0.9) : alpha('#000', 0.87),
            color: isDark ? '#000' : '#fff',
            border: 'none', outline: 'none',
            transition: 'opacity 0.15s',
            '&:hover:not(:disabled)': { opacity: 0.87 },
            '&:disabled': { opacity: 0.35, cursor: 'not-allowed' },
          }}
        >
          <PersonAddOutlinedIcon sx={{ fontSize: 17 }} />
          <span>{t("addCollaborator")}</span>
        </Box>
      </Box>

      <ShareContractModal
        open={isShareModalOpen}
        onClose={() => {
          setIsShareModalOpen(false);
          // Refresh to pick up any new external invitations
          fetchExistingCollaborators();
        }}
        onShare={handleShare}
        organizationName={currentUser?.organization_name}
        contractId={contractId}
      />

      <Snackbar open={showSuccessAlert} autoHideDuration={6000} onClose={() => setShowSuccessAlert(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setShowSuccessAlert(false)} severity="success" sx={{ width: '100%', borderRadius: '10px' }}>{alertMessage}</Alert>
      </Snackbar>
      <Snackbar open={showErrorAlert} autoHideDuration={6000} onClose={() => setShowErrorAlert(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setShowErrorAlert(false)} severity="error" sx={{ width: '100%', borderRadius: '10px' }}>{alertMessage}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Collaboration;
